import { Box, Button, Flex, Icon, Text } from '@chakra-ui/react';
import React, { useState } from 'react';
import { IoMdCheckmarkCircle } from 'react-icons/io';
import { getAuthToken } from '../Helpers/script';
import { useAuth } from '../../Context/AuthContext';
import axios from 'axios';
import { useHistory } from 'react-router-dom';

const planConfig = {
  freePlan: {
    title: 'Basic',
    price: 'Free',
    duration: 'forever',
    features: [
      '10 credits/day',
      'SMART Notes',
      'Homework Helper',
      'Test Me Mode',
      'Class Chatbots',
    ],
  },
  proPlan: {
    title: 'Pro',
    price: '$6.99',
    duration: '/monthly',
    features: [
      'Unlimited credits',
      'Priority AI generation & speeds',
      'Early access to latest features',
      'Everything included in Basic',
    ],
  },
};

const PricingCard = ({ planId, isCurrentPlan }) => {
  const bgColor = planId === 'freePlan' ? 'white' : '#4A3AFF';
  const invertBgColor = planId === 'freePlan' ? '#4A3AFF' : 'white';
  const textColor = planId === 'freePlan' ? '#170F49' : 'white';
  const dimTextColor = planId === 'freePlan' ? '#6F6C90' : '#D9DBE9';
  const checkboxColor = planId === 'freePlan' ? '#4A3AFF' : '#ffffff';

  const [loading, setLoading] = useState(false);

  const { currentUser, setTopLevelError } = useAuth();
  const history = useHistory();

  const handleClick = async () => {
    if (!currentUser) return;
    if (planId === 'proPlan') {
      setLoading(true);
      const pageType = isCurrentPlan ? 'portal' : 'checkout';

      try {
        const token = await getAuthToken(currentUser);

        const response = await axios.get(
          `${
            process.env.REACT_APP_API_URL ?? ''
          }/api/create-${pageType}-session`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        // Handle success response here if necessary

        if (response.status === 200) {
          const { redirect_url } = response.data;

          window.open(redirect_url, '_blank');
        }
      } catch (err) {
        console.log(err);
        setTopLevelError(
          `Couldn't proceed with ${pageType} session. Please try again later.`
        );
      }
      setLoading(false);
    }
  };

  return (
    <Flex
      color={textColor}
      direction="column"
      border="1px solid #EFF0F6"
      borderRadius="24px"
      boxShadow="0px 2px 12px 0px #14142B14"
      p="2rem 1.8rem"
      w={{ base: '16rem', sm: '20rem' }}
      bg={bgColor}
      position="relative"
    >
      {planId === 'proPlan' && (
        <Box
          position="absolute"
          fontSize="0.8rem"
          fontWeight="100"
          top="1rem"
          right="1rem"
          background="#FFFFFF33"
          borderRadius="10px"
          p="8px 12px"
          display={{ base: 'none', sm: 'block' }}
        >
          Limited Deal
        </Box>
      )}

      <Flex>
        {planId === 'freePlan' ? (
          <svg
            width="72"
            height="72"
            viewBox="0 0 72 72"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="72" height="72" rx="16" fill="#ECEBFF" />
            <path
              d="M36 17C30.9609 17 26.1282 19.0018 22.565 22.565C19.0018 26.1282 17 30.9609 17 36C17 41.0391 19.0018 45.8718 22.565 49.435C26.1282 52.9982 30.9609 55 36 55L36 36V17Z"
              fill="#4A3AFF"
            />
            <path
              d="M36 55C41.0391 55 45.8718 52.9982 49.435 49.435C52.9982 45.8718 55 41.0391 55 36C55 30.9609 52.9982 26.1282 49.435 22.565C45.8718 19.0018 41.0391 17 36 17L36 36L36 55Z"
              fill="#B8B1FF"
            />
          </svg>
        ) : (
          <svg
            width="72"
            height="72"
            viewBox="0 0 72 72"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="72" height="72" rx="16" fill="white" />
            <rect x="17" y="17" width="18.4865" height="38" fill="#4A3AFF" />
            <rect
              x="35.4863"
              y="17"
              width="19.5135"
              height="38"
              fill="#B8B1FF"
            />
            <rect
              x="35.4863"
              y="35.4863"
              width="19.5135"
              height="19.5135"
              fill="#DCD8FF"
            />
          </svg>
        )}

        <Flex direction="column" ml="13px">
          <Text color={dimTextColor} fontWeight="500" fontSize="0.9rem">
            Studywise
          </Text>
          <Text fontSize="1.2rem">{planConfig[planId].title}</Text>
        </Flex>
      </Flex>
      <Flex m="0.7rem 0">
        <Box>
          <Text fontSize="2.4rem">{planConfig[planId].price}</Text>
        </Box>
        <Box display="flex" alignItems="flex-end" mb="0.7rem" ml="7px">
          <Text color={dimTextColor} fontSize="1rem" fontWeight="500">
            {planConfig[planId].duration}
          </Text>
        </Box>
      </Flex>
      <Flex
        fontWeight="700"
        fontSize="1rem"
        direction="column"
        alignItems="flex-start"
        height="11.9rem"
      >
        <Text letterSpacing="0.3px" fontSize="0.9rem">
          What's included
        </Text>
        {planConfig[planId].features.map((item, index) => (
          <Flex justifyContent="center" alignItems="center" p="4px">
            <Icon
              h="1.5rem"
              w="1.4rem"
              color={checkboxColor}
              as={IoMdCheckmarkCircle}
            />
            <Text key={index} fontWeight="400" ml="5px" fontSize="0.8rem">
              {item}
            </Text>
          </Flex>
        ))}
      </Flex>
      <Button
        m="1.5rem 0"
        bg={invertBgColor}
        color={bgColor}
        _hover={{ opacity: '85%' }}
        _active={{ opacity: '75%' }}
        onClick={handleClick}
        isLoading={loading}
      >
        {isCurrentPlan ? 'Your current plan' : 'Get started'}
      </Button>
    </Flex>
  );
};

export default PricingCard;
